
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useToast } from "@/hooks/use-toast"
import { apiClient } from "@/lib/api-client"

interface DeleteUserDialogProps {
  userId: string
  userName?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  onDeleted?: () => void
}

export function DeleteUserDialog({
  userId,
  userName,
  open,
  onOpenChange,
  onSuccess,
  onDeleted,
}: DeleteUserDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)

  const handleDelete = async () => {
    if (!userId) return

    try {
      setIsLoading(true)

      // Make a direct API call to delete the user
      await apiClient.delete(`/users/${userId}`)

      toast({
        title: t("common.success"),
        description: t("users.userDeleted"),
      })

      // Close the dialog first
      onOpenChange(false)

      // Then notify parent of success - use both callbacks for compatibility
      setTimeout(() => {
        if (onDeleted) onDeleted()
        if (onSuccess) onSuccess()

        // Ensure pointer events are restored
        document.body.style.pointerEvents = "auto"

        console.log("Delete success callback triggered")
      }, 100)
    } catch (err) {
      console.error("Error deleting user:", err)

      toast({
        title: t("common.error"),
        description: t("users.deleteError"),
        variant: "destructive",
      })

      // Close the dialog even on error
      onOpenChange(false)

      // Ensure pointer events are restored
      document.body.style.pointerEvents = "auto"
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(newOpen) => {
        onOpenChange(newOpen)
        if (!newOpen) {
          // Ensure pointer events are restored when dialog closes
          document.body.style.pointerEvents = "auto"
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("users.deleteUser")}</AlertDialogTitle>
          <AlertDialogDescription>
            {userName ? t("users.deleteConfirmNamed", { name: userName }) : t("users.deleteConfirm")}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              handleDelete()
            }}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading ? t("common.deleting") : t("common.delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
